// src/pages/CoilInEntry.js
/* =========================
UPDATED: Added new weight fields per business rules:
- party_declared_weight_kg (mandatory)
- party_weight_type (NET/GROSS/UNKNOWN)
- factory_gross_weight_kg, factory_net_weight_kg (optional)
- weight_status badges
- lifecycle_state display
- OCR Upload integration (Phase-3A)
========================= */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api, getFriendlyErrorMessage } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Package, Upload, Layers, AlertTriangle, Scale, CheckCircle2, ExternalLink } from 'lucide-react';
import OCRUpload from '../components/OCRUpload';

const toISOAtMidnight = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return null;
  const d = new Date(`${yyyy_mm_dd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const parseOptionalNumber = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
};

const todayDateStr = () => new Date().toISOString().slice(0, 10);

const safeUUID = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const COIL_IN_DRAFT_KEY = 'coildock:coilin:draft:v1';

// Program rows = circle size + optional rate
const makeProgramRow = () => ({ size_in: '', rate_per_kg: '' });

const normalizeProgramRows = (rows) => {
  const cleaned = (rows || [])
    .map((r) => ({
      size: r.size_in === '' ? null : Number(r.size_in),
      rate: r.rate_per_kg === '' ? null : Number(r.rate_per_kg),
    }))
    .filter((x) => Number.isFinite(x.size) && x.size > 0);

  if (!cleaned.length) return { circle_sizes: null, rates_by_circle_size: null };

  const seen = new Set();
  const circle_sizes = [];
  for (const x of cleaned) {
    if (!seen.has(x.size)) {
      seen.add(x.size);
      circle_sizes.push(x.size);
    }
  }

  const rates_by_circle_size = {};
  for (const x of cleaned) {
    if (x.rate === null) continue;
    if (!Number.isFinite(x.rate) || x.rate < 0) {
      throw new Error('Program rate must be a valid non-negative number');
    }
    rates_by_circle_size[String(x.size)] = x.rate;
  }

  return {
    circle_sizes,
    rates_by_circle_size: Object.keys(rates_by_circle_size).length ? rates_by_circle_size : null,
  };
};

const getSingleSizeOrNull = (rows) => {
  try {
    const { circle_sizes } = normalizeProgramRows(rows);
    if (!circle_sizes || circle_sizes.length !== 1) return null;
    return circle_sizes[0];
  } catch {
    return null;
  }
};

// Weight Status Badge Component
const WeightStatusBadge = ({ status, chargeSource }) => {
  if (status === 'NET_CONFIRMED') {
    return (
      <div className="flex items-center gap-1.5 font-mono text-[10px] text-[#00E676] uppercase font-bold">
        <span className="led-indicator text-[#00E676] bg-[#00E676]" />
        NET CONFIRMED
      </div>
    );
  }
  if (status === 'GROSS_ONLY') {
    return (
      <div className="flex items-center gap-1.5 font-mono text-[10px] text-blue-400 uppercase font-bold">
        <span className="led-indicator text-blue-400 bg-blue-400" />
        GROSS ONLY
      </div>
    );
  }
  // NOT_WEIGHED or unknown
  return (
    <div className="flex items-center gap-1.5 font-mono text-[10px] text-[#FFB300] uppercase font-bold">
      <span className="led-indicator text-[#FFB300] bg-[#FFB300]" />
      PENDING
    </div>
  );
};

// Charge Weight Source Badge
const ChargeSourceBadge = ({ source }) => {
  if (source === 'FACTORY_NET') {
    return <span className="font-mono text-[10px] bg-industrial-s2 border border-industrial-border text-slate-400 px-1.5 py-0.5 rounded-[2px] uppercase">Factory Net</span>;
  }
  if (source === 'FACTORY_GROSS_MINUS_PACKING') {
    return <span className="font-mono text-[10px] bg-industrial-s2 border border-industrial-border text-slate-400 px-1.5 py-0.5 rounded-[2px] uppercase">Gross - Packing</span>;
  }
  if (source === 'PARTY_DECLARED') {
    return (
      <span className="font-mono text-[10px] bg-[#FF1744]/15 border border-[#FF1744]/30 text-[#FF1744] px-1.5 py-0.5 rounded-[2px] uppercase font-bold inline-flex items-center gap-1">
        <AlertTriangle className="h-3 w-3" />
        PARTY DECLARED (RISK)
      </span>
    );
  }
  return null;
};

// Lifecycle Badge
const LifecycleBadge = ({ state }) => {
  if (state === 'ISSUED') {
    return (
      <div className="flex items-center gap-1.5 font-mono text-[10px] text-[#FFB300] uppercase font-bold">
        <span className="led-indicator text-[#FFB300] bg-[#FFB300]" />
        ISSUED
      </div>
    );
  }
  if (state === 'CLOSED') {
    return (
      <div className="flex items-center gap-1.5 font-mono text-[10px] text-slate-500 uppercase font-bold">
        <span className="led-indicator text-slate-500 bg-slate-500" />
        CLOSED
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 font-mono text-[10px] text-[#00E676] uppercase font-bold">
      <span className="led-indicator text-[#00E676] bg-[#00E676]" />
      OPEN
    </div>
  );
};

const CoilInEntry = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [parties, setParties] = useState([]);
  const [coilIns, setCoilIns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [slipFiles, setSlipFiles] = useState([]);
  
  // OCR state (Phase-3A)
  const [ocrData, setOcrData] = useState({ rawText: '', fileUrl: '' });

  const [formData, setFormData] = useState(() => {
    const today = todayDateStr();
    return {
      party_id: '',
      inward_date: today,
      slip_no: '',
      slip_remarks: '', // For OCR text
      coils: [
        {
          coil_id: safeUUID(),
          thickness_mm: '',
          coil_width_mm: '',
          party_declared_weight_kg: '',
          party_weight_type: 'UNKNOWN',
          factory_gross_weight_kg: '',
          factory_net_weight_kg: '',
          packing_deduction_kg: '',
          kanta_files: [],
          notes: '',
          program_rows: [makeProgramRow()],
          program_notes: '',
          program_planned_kg: '',
        },
      ],
    };
  });

  const canCreate = hasRole(['ADMIN', 'OWNER', 'MUNIM']);
  
  // Handle OCR result - prefill remarks with raw text
  const handleOCRResult = ({ rawText, fileUrl }) => {
    setOcrData({ rawText, fileUrl });
    if (rawText) {
      setFormData(prev => ({
        ...prev,
        slip_remarks: rawText.slice(0, 500) // Limit for display
      }));
    }
  };

  useEffect(() => {
    fetchParties();
    fetchCoilIns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(COIL_IN_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      const hasDraft = draft && draft.formData && Array.isArray(draft.formData.coils);
      if (!hasDraft) return;
      if (window.confirm('Found unsaved Coil In draft. Restore it?')) {
        setFormData(draft.formData);
        if (draft.ocrData) setOcrData(draft.ocrData);
        toast.info('Draft restored');
      }
    } catch {
      // ignore invalid draft
    }
  }, []);

  useEffect(() => {
    try {
      const draft = {
        formData: {
          ...formData,
          coils: (formData.coils || []).map((c) => ({
            ...c,
            kanta_files: [],
          })),
        },
        ocrData,
      };
      localStorage.setItem(COIL_IN_DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // ignore storage errors
    }
  }, [formData, ocrData]);

  const fetchParties = async () => {
    let lastError = null;
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const response = await api.parties.list({ is_active: true });
        setParties(response.data || []);
        return;
      } catch (err) {
        lastError = err;
        if (attempt < 2) {
          await sleep(1200);
        }
      }
    }
    const message = getFriendlyErrorMessage(lastError, 'Failed to fetch parties');
    toast.error(message, {
      action: { label: 'Retry', onClick: () => fetchParties() },
    });
  };

  const fetchCoilIns = async () => {
    try {
      const response = await api.coilIns.list({ is_active: true });
      setCoilIns(response.data || []);
    } catch (err) {
      toast.error(getFriendlyErrorMessage(err, 'Failed to fetch coil entries'), {
        action: { label: 'Retry', onClick: () => fetchCoilIns() },
      });
    }
  };

  const addCoil = () => {
    setFormData((prev) => ({
      ...prev,
      coils: [
        ...prev.coils,
        {
          coil_id: safeUUID(),
          thickness_mm: '',
          coil_width_mm: '',
          party_declared_weight_kg: '',
          party_weight_type: 'UNKNOWN',
          factory_gross_weight_kg: '',
          factory_net_weight_kg: '',
          packing_deduction_kg: '',
          kanta_files: [],
          notes: '',
          program_rows: [makeProgramRow()],
          program_notes: '',
          program_planned_kg: '',
        },
      ],
    }));
  };

  const removeCoil = (index) => {
    setFormData((prev) => ({
      ...prev,
      coils: prev.coils.filter((_, i) => i !== index),
    }));
  };

  const updateCoil = (index, field, value) => {
    setFormData((prev) => {
      const next = [...prev.coils];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, coils: next };
    });
  };

  const addProgramRow = (coilIndex) => {
    setFormData((prev) => {
      const coils = [...prev.coils];
      const c = { ...coils[coilIndex] };
      c.program_rows = [...(c.program_rows || []), makeProgramRow()];
      coils[coilIndex] = c;
      return { ...prev, coils };
    });
  };

  const removeProgramRow = (coilIndex, rowIndex) => {
    setFormData((prev) => {
      const coils = [...prev.coils];
      const c = { ...coils[coilIndex] };
      const rows = (c.program_rows || []).filter((_, i) => i !== rowIndex);
      c.program_rows = rows.length ? rows : [makeProgramRow()];
      coils[coilIndex] = c;

      const singleSize = getSingleSizeOrNull(c.program_rows);
      if (!singleSize && c.program_planned_kg) c.program_planned_kg = '';

      return { ...prev, coils };
    });
  };

  const updateProgramRow = (coilIndex, rowIndex, field, value) => {
    setFormData((prev) => {
      const coils = [...prev.coils];
      const c = { ...coils[coilIndex] };
      const rows = [...(c.program_rows || [])];
      rows[rowIndex] = { ...rows[rowIndex], [field]: value };
      c.program_rows = rows;

      const singleSize = getSingleSizeOrNull(c.program_rows);
      if (!singleSize && c.program_planned_kg) {
        c.program_planned_kg = '';
      }

      coils[coilIndex] = c;
      return { ...prev, coils };
    });
  };

  const resetForm = () => {
    const today = todayDateStr();
    setSlipFiles([]);
    setOcrData({ rawText: '', fileUrl: '' }); // Reset OCR data
    setFormData({
      party_id: '',
      inward_date: today,
      slip_no: '',
      slip_remarks: '',
      coils: [
        {
          coil_id: safeUUID(),
          thickness_mm: '',
          coil_width_mm: '',
          party_declared_weight_kg: '',
          party_weight_type: 'UNKNOWN',
          factory_gross_weight_kg: '',
          factory_net_weight_kg: '',
          packing_deduction_kg: '',
          kanta_files: [],
          notes: '',
          program_rows: [makeProgramRow()],
          program_notes: '',
          program_planned_kg: '',
        },
      ],
    });
    localStorage.removeItem(COIL_IN_DRAFT_KEY);
  };

  const uploadMany = async (files) => {
    if (!files || files.length === 0) return [];
    const urls = [];
    for (const f of files) {
      const res = await api.upload(f);
      const url = res?.data?.url;
      if (url) urls.push(url);
    }
    return urls;
  };

  const buildProgramSeed = (rows, plannedKgRaw, programNotes) => {
    const { circle_sizes, rates_by_circle_size } = normalizeProgramRows(rows);

    if (!circle_sizes || circle_sizes.length === 0) return null;

    let planned_kg = null;
    if (circle_sizes.length === 1) {
      const pk = parseOptionalNumber(plannedKgRaw);
      if (pk !== null) {
        if (!Number.isFinite(pk) || pk <= 0) {
          throw new Error('Program planned kg (if provided) must be > 0');
        }
        planned_kg = pk;
      }
    }

    return {
      circle_sizes,
      rates_by_circle_size,
      planned_kg,
      program_notes: programNotes?.trim() ? programNotes.trim() : null,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!formData.party_id) return toast.error('Please select a party');
    if (!formData.inward_date) return toast.error('Please select inward date');

    setLoading(true);
    try {
      const inwardISO = toISOAtMidnight(formData.inward_date);
      if (!inwardISO) return toast.error('Invalid inward date');

      const slipDateISO = inwardISO;
      // Use OCR uploaded file URL if available, otherwise upload slip files
      let slip_photo_urls = ocrData.fileUrl ? [ocrData.fileUrl] : [];
      if (!ocrData.fileUrl && slipFiles.length > 0) {
        slip_photo_urls = await uploadMany(slipFiles);
      }

      const coilsPayload = [];

      for (const coil of formData.coils) {
        const thickness = parseOptionalNumber(coil.thickness_mm);
        const width = parseOptionalNumber(coil.coil_width_mm);
        const partyDeclaredWeight = parseOptionalNumber(coil.party_declared_weight_kg);

        if (thickness === null || width === null || partyDeclaredWeight === null) {
          throw new Error('Please fill Thickness, Width, and Party Declared Weight for all coils');
        }

        const coilId = coil.coil_id || safeUUID();

        const factoryGross = parseOptionalNumber(coil.factory_gross_weight_kg);
        const factoryNet = parseOptionalNumber(coil.factory_net_weight_kg);
        const packingDeduction = parseOptionalNumber(coil.packing_deduction_kg);
        const kanta_photo_urls = await uploadMany(coil.kanta_files);

        const programSeed = buildProgramSeed(coil.program_rows, coil.program_planned_kg, coil.program_notes);

        const finalPlannedKg =
          programSeed?.circle_sizes?.length === 1 ? (programSeed?.planned_kg || null) : null;

        coilsPayload.push({
          coil_id: coilId,
          thickness_mm: thickness,
          coil_width_mm: width,
          // New weight fields
          party_declared_weight_kg: partyDeclaredWeight,
          party_weight_type: coil.party_weight_type || 'UNKNOWN',
          factory_gross_weight_kg: factoryGross,
          factory_net_weight_kg: factoryNet,
          packing_deduction_kg: packingDeduction,
          // Legacy fields (for backward compatibility)
          slip_weight: partyDeclaredWeight,
          kanta_weight: factoryNet,
          kanta_photos: kanta_photo_urls,
          notes: coil.notes?.trim() ? coil.notes.trim() : null,
          // Program seed fields
          circle_sizes: programSeed?.circle_sizes || null,
          rates_by_circle_size: programSeed?.rates_by_circle_size || null,
          planned_kg: finalPlannedKg,
          program_notes: programSeed?.program_notes || null,
        });
      }

      const payload = {
        party_id: formData.party_id,
        inward_datetime: inwardISO,
        slip_no: formData.slip_no?.trim() ? formData.slip_no.trim() : null,
        slip_date: slipDateISO,
        slip_photos: slip_photo_urls,
        coils: coilsPayload,
      };

      await api.coilIns.create(payload);

      // Submit OCR feedback if we have OCR data (Phase-3A)
      if (ocrData.fileUrl && ocrData.rawText) {
        try {
          await api.ocr.feedback({
            context_type: 'COIL_IN',
            vendor_or_party_id: formData.party_id || null,
            file_url: ocrData.fileUrl,
            raw_text: ocrData.rawText,
            extracted_fields: {}, // Phase-3B will add smart extraction
            final_fields: {
              party_id: formData.party_id,
              slip_no: formData.slip_no,
              inward_date: formData.inward_date,
              coils_count: coilsPayload.length,
              slip_remarks: formData.slip_remarks,
            },
          });
        } catch (feedbackErr) {
          console.warn('OCR feedback submission failed (non-critical):', feedbackErr);
        }
      }

      toast.success('Coil entry created successfully');
      localStorage.removeItem(COIL_IN_DRAFT_KEY);
      resetForm();
      fetchCoilIns();
    } catch (error) {
      toast.error(getFriendlyErrorMessage(error, error?.message || 'Failed to create coil entry'));
    } finally {
      setLoading(false);
    }
  };

  const renderProgramUI = (coil, coilIndex) => {
    const singleSize = getSingleSizeOrNull(coil.program_rows);

    return (
      <div className="mt-6 border-t border-industrial-border pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-accent" />
            <p className="font-mono text-xs font-bold uppercase text-white">Program Sheet (Optional)</p>
          </div>
          <button 
            type="button" 
            className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none py-1.5 px-3 font-mono text-[10px] uppercase tracking-wider flex items-center gap-1 transition-colors duration-75"
            onClick={() => addProgramRow(coilIndex)}
          >
            <Plus className="h-3.5 w-3.5" />
            Add Size
          </button>
        </div>

        <p className="text-[10px] font-mono text-slate-500 mb-3 uppercase tracking-wider">
          Sizes + optional rates. Planned Kg is asked ONLY when exactly one size is entered.
        </p>

        <div className="space-y-2">
          {(coil.program_rows || []).map((row, rowIndex) => (
            <div key={rowIndex} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
              <div className="text-left">
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Circle Size (in)</Label>
                <Input
                  type="number"
                  step="0.1"
                  className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full"
                  value={row.size_in}
                  onChange={(e) => updateProgramRow(coilIndex, rowIndex, 'size_in', e.target.value)}
                  placeholder="e.g. 8.0"
                />
              </div>

              <div className="text-left">
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Rate (₹/kg)</Label>
                <Input
                  type="number"
                  step="0.01"
                  className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full"
                  value={row.rate_per_kg}
                  onChange={(e) => updateProgramRow(coilIndex, rowIndex, 'rate_per_kg', e.target.value)}
                  placeholder="optional"
                />
              </div>

              <div className="flex justify-end">
                {(coil.program_rows || []).length > 1 && (
                  <button
                    type="button"
                    className="bg-industrial-s1 border border-industrial-border hover:bg-neutral-800 text-white rounded-none p-2.5 font-mono text-xs"
                    onClick={() => removeProgramRow(coilIndex, rowIndex)}
                  >
                    <Trash2 className="h-4 w-4 text-[#FF1744]" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {singleSize ? (
          <div className="mt-3 text-left">
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Planned Kg (for {singleSize}&quot; only)</Label>
            <Input
              type="number"
              step="0.01"
              className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full"
              value={coil.program_planned_kg || ''}
              onChange={(e) => updateCoil(coilIndex, 'program_planned_kg', e.target.value)}
              placeholder="optional"
            />
          </div>
        ) : (
          <div className="mt-3 text-[10px] font-mono text-slate-500 uppercase tracking-wider text-left">
            Multiple sizes detected → planned kg is not applicable here.
          </div>
        )}

        <div className="mt-3 text-left">
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Program Notes (Optional)</Label>
          <Input
            className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full"
            value={coil.program_notes || ''}
            onChange={(e) => updateCoil(coilIndex, 'program_notes', e.target.value)}
            placeholder="Any reference note"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 text-left" data-testid="coil-in-page">
      <div>
        <h1 className="font-mono text-2xl font-bold uppercase tracking-wider text-white">
          Coil Inward Register
        </h1>
        <p className="text-xs font-mono text-slate-400 mt-1 uppercase tracking-wider">Record inward raw coil batches from parties</p>
      </div>

      {canCreate ? (
        <div className="bg-industrial-s1 border border-industrial-border p-6 rounded-none text-left">
          <div className="mb-6 border-b border-industrial-border pb-3">
            <h3 className="font-mono text-base font-bold uppercase text-white">New Batch Entry</h3>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Slip Details */}
            <div className="border-b border-industrial-border pb-6">
              <h3 className="font-mono text-sm font-bold uppercase text-white mb-4">Slip Specifications</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="party" className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Party *</Label>
                  <select
                    id="party"
                    data-testid="party-select"
                    value={formData.party_id}
                    onChange={(e) => setFormData({ ...formData, party_id: e.target.value })}
                    className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
                    required
                  >
                    <option value="">Select party</option>
                    {parties.map((party) => (
                      <option key={party.id} value={party.id}>
                        {party.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="inward_date" className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Inward Date *</Label>
                  <Input
                    id="inward_date"
                    type="date"
                    data-testid="inward-date-input"
                    value={formData.inward_date}
                    onChange={(e) => setFormData({ ...formData, inward_date: e.target.value })}
                    required
                    className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="slip_no" className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Slip No. (Optional)</Label>
                  <Input
                    id="slip_no"
                    value={formData.slip_no}
                    onChange={(e) => setFormData({ ...formData, slip_no: e.target.value })}
                    className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full"
                    placeholder="Enter slip identifier"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-2">
                    <Upload className="h-4 w-4" /> Slip Photos (Optional - without OCR)
                  </Label>
                  <Input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => setSlipFiles(Array.from(e.target.files || []))}
                    disabled={!!ocrData.fileUrl}
                    className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full file:bg-industrial-s1 file:border-0 file:text-white file:font-mono file:text-xs file:uppercase"
                  />
                </div>
              </div>

              {/* OCR Upload Section (Phase-3A) */}
              <div className="mt-4 pt-4 border-t border-industrial-border">
                <OCRUpload
                  contextType="COIL_IN"
                  partyId={formData.party_id}
                  onResult={handleOCRResult}
                  label="Upload Slip (OCR) - Preferred"
                />
                {formData.slip_remarks && (
                  <div className="mt-3">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">OCR Extracted Text (Review)</Label>
                    <textarea
                      className="w-full mt-1 p-2 text-xs font-mono border border-industrial-border bg-industrial-s2 text-white focus:outline-none focus:border-accent rounded-none min-h-[60px]"
                      value={formData.slip_remarks}
                      onChange={(e) => setFormData({ ...formData, slip_remarks: e.target.value })}
                      placeholder="OCR text will appear here for review..."
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Coils */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-mono text-sm font-bold uppercase text-white">Coils In Batch</h3>
                <button 
                  type="button" 
                  onClick={addCoil} 
                  data-testid="add-coil-button"
                  className="bg-accent hover:bg-accent-hover text-white font-mono text-xs font-bold uppercase tracking-wider py-2 px-3 rounded-none border-b-2 border-black/30 flex items-center gap-1.5 transition-colors duration-75"
                >
                  <Plus className="h-4 w-4" />
                  Add Coil
                </button>
              </div>

              <div className="space-y-4">
                {formData.coils.map((coil, index) => (
                  <div key={coil.coil_id || index} className="bg-industrial-s2 border border-industrial-border p-6 rounded-none text-left mb-6">
                    <div className="flex items-start justify-between border-b border-industrial-border pb-3 mb-4">
                      <h4 className="font-mono text-sm font-bold uppercase text-white">Coil #{index + 1}</h4>
                      {formData.coils.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCoil(index)}
                          data-testid={`remove-coil-${index}`}
                          className="bg-industrial-s1 border border-industrial-border hover:bg-neutral-800 text-white rounded-none p-2 font-mono text-xs uppercase"
                        >
                          <Trash2 className="h-4 w-4 text-[#FF1744]" />
                        </button>
                      )}
                    </div>

                    {/* Dimensions */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                      <div className="space-y-1">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Thickness (mm) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          data-testid={`thickness-${index}`}
                          value={coil.thickness_mm}
                          onChange={(e) => updateCoil(index, 'thickness_mm', e.target.value)}
                          required
                          className="bg-industrial-s1 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full"
                          placeholder="e.g. 2.50"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Width (mm) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={coil.coil_width_mm}
                          onChange={(e) => updateCoil(index, 'coil_width_mm', e.target.value)}
                          required
                          className="bg-industrial-s1 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full"
                          placeholder="e.g. 1250"
                        />
                      </div>

                      <div className="col-span-2 md:col-span-1 space-y-1">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Notes</Label>
                        <Input
                          value={coil.notes}
                          onChange={(e) => updateCoil(index, 'notes', e.target.value)}
                          className="bg-industrial-s1 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full"
                          placeholder="e.g. Minor scratches"
                        />
                      </div>
                    </div>

                    {/* Weight Section */}
                    <div className="border border-industrial-border p-4 bg-industrial-s1 text-left rounded-none mt-4">
                      <h5 className="font-mono text-xs font-bold uppercase text-white flex items-center gap-2 mb-3 pb-1 border-b border-industrial-border/30">
                        <Scale className="h-4 w-4 text-accent" />
                        Weight Specifications
                      </h5>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Party Declared Weight (kg) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full"
                            value={coil.party_declared_weight_kg}
                            onChange={(e) => updateCoil(index, 'party_declared_weight_kg', e.target.value)}
                            required
                            placeholder="Declared weight"
                          />
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Party Weight Type</Label>
                          <select
                            value={coil.party_weight_type}
                            onChange={(e) => updateCoil(index, 'party_weight_type', e.target.value)}
                            className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
                          >
                            <option value="UNKNOWN">Unknown</option>
                            <option value="NET">Net</option>
                            <option value="GROSS">Gross</option>
                          </select>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Factory Gross (kg)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full"
                            value={coil.factory_gross_weight_kg}
                            onChange={(e) => updateCoil(index, 'factory_gross_weight_kg', e.target.value)}
                            placeholder="Optional"
                          />
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Factory Net (kg)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full"
                            value={coil.factory_net_weight_kg}
                            onChange={(e) => updateCoil(index, 'factory_net_weight_kg', e.target.value)}
                            placeholder="Optional"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div className="space-y-1">
                          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Packing Deduction (kg)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full"
                            value={coil.packing_deduction_kg}
                            onChange={(e) => updateCoil(index, 'packing_deduction_kg', e.target.value)}
                            placeholder="For gross to net"
                          />
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Weighment Photos</Label>
                          <Input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) => updateCoil(index, 'kanta_files', Array.from(e.target.files || []))}
                            className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full file:bg-industrial-s1 file:border-0 file:text-white file:font-mono file:text-xs file:uppercase"
                          />
                        </div>
                      </div>

                      <p className="text-[10px] font-mono text-slate-500 mt-2 uppercase tracking-wider">
                        Factory weighment can be added later. Coils without factory weight will show as &quot;Weighment Pending&quot;.
                      </p>
                    </div>

                    {/* Program per coil */}
                    {renderProgramUI(coil, index)}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-6 border-t border-industrial-border pt-4">
              <button 
                type="button" 
                className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none py-2.5 px-4 font-mono text-xs uppercase tracking-wider transition-colors duration-75"
                onClick={resetForm}
              >
                Reset Form
              </button>
              <button 
                type="submit" 
                className="bg-accent hover:bg-accent-hover text-white font-mono text-xs font-bold uppercase tracking-wider py-2.5 px-4 rounded-none border-b-2 border-black/30 transition-colors duration-75"
                disabled={loading} 
                data-testid="submit-coil-entry"
              >
                {loading ? 'Creating...' : 'Create Inward Entry'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-industrial-s1 border border-[#FF1744] p-6 rounded-none text-center">
          <p className="font-mono text-xs uppercase tracking-wider text-[#FF1744]">You do not have permission to create coil entries</p>
        </div>
      )}

      {/* Recent Entries with Weight Status Badges */}
      <div className="bg-industrial-s1 border border-industrial-border p-6 rounded-none text-left mt-6">
        <div className="mb-4 border-b border-industrial-border pb-2">
          <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-white flex items-center gap-2">
            <Package className="h-5 w-5 text-accent" />
            Recent Inward Batches
          </h3>
        </div>
        <div>
          {coilIns.length === 0 ? (
            <div className="text-center py-8 text-xs font-mono text-slate-500 uppercase tracking-wider">No coil entries yet</div>
          ) : (
            <div className="space-y-4">
              {coilIns.slice(0, 10).map((coilIn) => {
                // Calculate totals and count pending weighments
                let totalChargeWeight = 0;
                let pendingWeighmentCount = 0;
                let riskCount = 0;

                (coilIn.coils || []).forEach((c) => {
                  const chargeWeight = c.charge_weight_kg || c.factory_net_weight_kg || c.kanta_weight || c.party_declared_weight_kg || c.slip_weight || 0;
                  totalChargeWeight += parseFloat(chargeWeight) || 0;

                  const status = c.weight_status || 'NOT_WEIGHED';
                  if (status === 'NOT_WEIGHED') pendingWeighmentCount++;
                  
                  if (c.charge_weight_source === 'PARTY_DECLARED') riskCount++;
                });

                return (
                  <div key={coilIn.id} className="bg-industrial-s2 border border-industrial-border p-4 rounded-none hover:border-[#FF6B00] transition-colors">
                    <div className="flex items-start justify-between border-b border-industrial-border/30 pb-3">
                      <div className="space-y-1">
                        <p className="font-sans text-xs text-white font-bold">{coilIn.party_name}</p>
                        <p className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">
                          RECEIVED: {new Date(coilIn.inward_datetime).toLocaleDateString()}
                        </p>
                        {coilIn.slip_no && (
                          <p className="text-[10px] font-mono text-slate-400">SLIP NO: <span className="text-white font-semibold">{coilIn.slip_no}</span></p>
                        )}
                        
                        {/* Status Badges */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {pendingWeighmentCount > 0 && (
                            <div className="flex items-center gap-1.5 font-mono text-[9px] text-[#FFB300] bg-[#FFB300]/10 border border-[#FFB300]/20 px-1.5 py-0.5 rounded-[2px] uppercase font-bold">
                              <AlertTriangle className="h-3 w-3" />
                              {pendingWeighmentCount} Weighment Pending
                            </div>
                          )}
                          {riskCount > 0 && (
                            <div className="flex items-center gap-1.5 font-mono text-[9px] text-[#FF1744] bg-[#FF1744]/10 border border-[#FF1744]/20 px-1.5 py-0.5 rounded-[2px] uppercase font-bold">
                              <AlertTriangle className="h-3 w-3" />
                              {riskCount} Declared (RISK)
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right font-mono text-xs">
                        <p className="text-white font-bold">{coilIn.coils.length} Coils</p>
                        <p className="text-slate-400 mt-1 uppercase tracking-wider text-[10px]">
                          Charge Wt: <span className="text-white font-bold">{totalChargeWeight.toFixed(2)} kg</span>
                        </p>
                      </div>
                    </div>

                    {/* Expanded coil details */}
                    <div className="mt-3 space-y-2">
                      {(coilIn.coils || []).map((coil, idx) => (
                        <div key={coil.coil_id || idx} className="flex flex-col md:flex-row md:items-center justify-between text-xs bg-industrial-s1 border border-industrial-border p-2.5 rounded-none font-mono gap-2">
                          <div className="flex items-center gap-3">
                            <span className="text-slate-500 font-bold">#{idx + 1}</span>
                            <span className="text-white">{coil.thickness_mm}mm × {coil.coil_width_mm}mm</span>
                            <WeightStatusBadge status={coil.weight_status} />
                            <LifecycleBadge state={coil.lifecycle_state} />
                          </div>
                          <div className="flex items-center justify-between md:justify-end gap-4 w-full md:w-auto">
                            <span className="text-white font-bold">
                              {(coil.charge_weight_kg || coil.factory_net_weight_kg || coil.kanta_weight || coil.party_declared_weight_kg || coil.slip_weight || 0).toFixed(2)} kg
                            </span>
                            <ChargeSourceBadge source={coil.charge_weight_source} />
                            <button
                              className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none h-8 px-3 text-[10px] uppercase tracking-wider flex items-center gap-1 transition-colors duration-75"
                              onClick={() => navigate(`/coil/${coilIn.id}/${coil.coil_id}`)}
                              data-testid={`view-coil-${coil.coil_id}`}
                            >
                              <ExternalLink className="h-3 w-3 text-accent" />
                              Open Coil
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoilInEntry;
